/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard 
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/

import { useEffect, useState, useContext } from "react";
import {AuthContext} from "context";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { Autocomplete } from "@mui/material";

// Material Dashboard React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// E4A React components
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import Footer from "e4acomponents/Footer";
import FormField from "layouts/applications/wizard/components/FormField";
import { useNavigate } from "react-router-dom";

// Service and components
import partCategoryService from "services/part-category-service";
import AttributeFields from "./attributeFields";

const NewPartCode = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [partCode, setPartCode] = useState({
    categoryCode: "",
    parentCategory: "",
    description: "",
    examples: "",
  });

  const [error, setError] = useState({
    categoryCode: false,
    parentCategory: false,
    description: false,
    examples: false,
  });

  const authContext = useContext(AuthContext);

  const [value, setValue] = useState("");

  const [numAttributes, setNumAttributes] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const [attributes, setAttributes] = useState({});
  const [disableAddAttribute, setDisableAddAttribute] = useState(true);
  const [existingCatCodes, setExistingCatCodes] = useState([]);
  const [orderChanged, setOrderChanged] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Fetch the parent categories
  useEffect(() => {
    (async () => {
      try {
        const response = await partCategoryService.getAllParentCategories();
        setCategories(response.data.attributes);
        const secondResponse = await partCategoryService.getCategoryCodes();
        setExistingCatCodes(secondResponse.data.attributes);
      } catch (err) {
        console.error(err);
        return null;
      }
    })();
  }, []);

  // Enable the add attribute button only if the last added attribute is filled
  useEffect(() => {
    if (attributes[numAttributes - 1] && attributes[numAttributes - 1].attribute) {
      setDisableAddAttribute(false);
    }
  }, [attributes]);

  // Update the value of the parentCategory when an option is selected
  useEffect(() => {
    changeHandler({ target: { name: "parentCategory", value: value } });
  }, [value]);

  //Handles the changes of the input fields
  const changeHandler = (e) => {
    setPartCode({
      ...partCode,
      [e.target.name]: e.target.value,
    });
  };

  // Helper function to reset the errors
  const resetErrors = () => {
    setError({
      categoryCode: false,
      parentCategory: false,
      description: false,
      examples: false,
      attributes: false
    });
  };

  const fieldsValidator = () => {
    // List of the required fields 
    const requiredFields = [
      ["categoryCode", "categoryCode"],
      ["parentCategory", "parentCategory"], 
      ["description", "description"],
      ["examples", "examples"],
    ];

    let isValid = true;
    let errorFields = {};

    // Iterates through the required fields to check if any of them is empty
    for (const [path, errorKey] of requiredFields) {
      const value = partCode[path];
      if (!value?.trim()) {
        if (errorKey === 'categoryCode') {
          errorFields[errorKey] = "The category code field is required";
        } else {
          errorFields[errorKey] = `The ${errorKey} field is required`; 
        }
        isValid = false;
      }
    }

    // Check if category code already exists
    if (partCode.categoryCode && existingCatCodes.includes(partCode.categoryCode.trim())) {
      errorFields.categoryCode = "This category code is already in use";
      isValid = false;
    }

    // Check if number of attributes matches
    const validAttributeKeys = Object.keys(partCode.attributes || {}).filter(key => !isNaN(key));
    if (validAttributeKeys.length !== numAttributes) {
      errorFields.attributes = "All attribute fields must be filled in";
      isValid = false;
    }

    setError(errorFields);
    return isValid;
  };

  // Handles the submission of the form
  const submitHandler = async (e) => {
    e.preventDefault();

    resetErrors();

    if (!fieldsValidator()) {
      return;
    }

    // Sanitize attributes before creating payload
    const sanitizedAttributes = Object.entries(partCode.attributes || {})
      .reduce((acc, [key, value]) => {
        if (value && value.attribute) {
          acc[key] = value;
        }
        return acc;
      }, {});

    const newPartCategory = {
      data: {
        type: "partCategory",
        attributes: {
          categoryCode: partCode.categoryCode.trim(),
          parentCategory: partCode.parentCategory.trim(),
          description: partCode.description.trim(),
          examples: partCode.examples.trim(),
          status: "Pending Approval",
          attributes: sanitizedAttributes,
          has_attributes: Object.keys(sanitizedAttributes).length > 0,
        },
        logs: {
          user: await authContext.getCurrentUser(),
          comment: ""
        }
      }
    };
    try {
      await partCategoryService.createPartCategory(newPartCategory);
      navigate("/part-code-management/part-category-view", {
        state: { value: false, text: "" },
      });
    } catch (err) {
      console.log(err);
      return null;
    }
  };

  const addAttributeHandler = () => {
    setNumAttributes(numAttributes + 1);
    setDisableAddAttribute(true);
  }
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <MDBox
              mt={6}
              mb={8}
              textAlign="center"
              sx={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 1s ease-in-out, transform 1s ease-in-out',
              }}
            >
              <MDBox mb={1}>
                <MDTypography variant="h3" fontWeight="bold">
                  Create A New Part Category.
                </MDTypography>
              </MDBox>
              <MDTypography variant="h5" fontWeight="regular" color="secondary">
                These informations will be used to describe the new part Category.
              </MDTypography>
            </MDBox>
            <Card
              sx={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 1s ease-in-out, transform 1s ease-in-out',
              }}
            >
              <MDBox
                component="form"
                method="POST"
                onSubmit={submitHandler}
                encType="multipart/form-data"
              >
                <MDBox display="flex" flexDirection="column" px={3} my={4}>
                  <Grid container spacing={3}>

                    {/* Category code field (e.g. CAP ) */}
                    <Grid item xs={12} md={6}>
                      <FormField
                        label="Part Category Code"
                        placeholder="XXX"
                        name="categoryCode"
                        value={partCode.categoryCode}
                        error={error.categoryCode}
                        onChange={changeHandler}
                        inputProps={{
                          autoComplete: "categoryCode",
                          form: {
                            autoComplete: "off",
                          },
                        }}
                      />
                      {error.categoryCode && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                          {error.categoryCode}
                        </MDTypography>
                      )}

                    </Grid>

                    {/* Parent category field */}
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        defaultValue=""
                        options={categories}
                        getOptionLabel={(option) => option}
                        error={error.parentCategory}
                        value={value ?? ""}
                        onChange={(event, newValue) => {
                          setValue(newValue);
                        }}
                        renderInput={(params) => (
                          <FormField {...params} label="Parent Category" InputLabelProps={{ shrink: true }} />
                        )}
                      />
                      {error.parentCategory && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                          {error.parentCategory}
                        </MDTypography>
                      )}
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} mt={2}>

                    {/* Full Part Category field */}
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Full Part Category"
                        placeholder=" "
                        name="description"
                        value={partCode.description}
                        onChange={changeHandler}
                        error={error.description}
                        multiline rows={5}
                        fullWidth
                        inputProps={{
                          autoComplete: "description",
                          form: {
                            autoComplete: "off",
                          },
                        }}
                      />
                      {error.description && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                          {error.description}
                        </MDTypography>
                      )}
                    </Grid>

                    {/* Examples and Notes field */}
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Examples and Notes"
                        placeholder=" "
                        name="examples"
                        value={partCode.examples}
                        onChange={changeHandler}
                        error={error.examples}
                        multiline rows={5}
                        fullWidth
                        inputProps={{
                          autoComplete: "examples",
                          form: {
                            autoComplete: "off",
                          },
                        }}
                      />
                      {error.examples && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                          {error.examples}
                        </MDTypography>
                      )}
                    </Grid>
                  </Grid>


                  {/* Attributes fields */}
                  <AttributeFields
                    numAttributes={numAttributes}
                    setNumAttributes={setNumAttributes}
                    error={error}
                    changeHandler={changeHandler}
                    attributes={attributes}
                    setAttributes={setAttributes}
                    setOrderChanged={setOrderChanged}
                  />

                  {error.attributes && (
                    <MDTypography variant="caption" color="error" fontWeight="light">
                      {error.attributes}
                    </MDTypography>
                  )}

                  <MDBox mt={2} mb={2} display="flex" justifyContent="space-between">
                    <MDBox>
                      <MDButton
                        variant="gradient"
                        color="dark"
                        size="small"
                        px={2}
                        mx={2}
                        onClick={addAttributeHandler}
                        disabled={disableAddAttribute}
                      >
                        Add an Attribute
                      </MDButton>
                    </MDBox>
                    <MDBox display="flex" justifyContent="flex-end">
                      <MDBox mx={2}>
                        <MDButton
                          variant="gradient"
                          color="dark"
                          size="small"
                          px={2}
                          mx={2}
                          onClick={() =>
                            navigate("/part-code-management/part-category-view", {
                              state: { value: false, text: "" },
                            })
                          }
                        >
                          Cancel
                        </MDButton>
                      </MDBox>

                      <MDButton variant="gradient" color="dark" size="small" type="submit">
                        Create
                      </MDButton>

                    </MDBox>

                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default NewPartCode;
